import { sessionRetryLink } from "../lib/sessionRetryLink.js";
import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import { rejectRateLimitedResponse } from "../lib/buildPolling.js";
import type { AppRouter } from "../routers/index.js";
import {
  ensureFreshSession,
  getAccessToken,
  refreshSession,
} from "../lib/auth.js";
import { clearCsrfToken, withCsrfHeaders } from "../lib/csrf.js";

function bearerHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const trpc = createTRPCProxyClient<AppRouter>({
  links: [
    sessionRetryLink<AppRouter>(),
    httpBatchLink({
      url: "/api/trpc",
      methodOverride: "POST",
      headers() {
        return bearerHeaders();
      },
      fetch(url, options) {
        const run = async (retried: boolean): Promise<Response> => {
          if (!retried) {
            await ensureFreshSession();
          }
          const token = getAccessToken();
          let headers = new Headers(options?.headers);
          if (token) headers.set("Authorization", `Bearer ${token}`);
          else headers.delete("Authorization");
          headers = await withCsrfHeaders(headers);
          const res = await fetch(url, {
            ...options,
            headers,
            credentials: "same-origin",
          });
          rejectRateLimitedResponse(res);
          if (res.status === 403 && !retried) {
            clearCsrfToken();
            return run(true);
          }
          if (res.status === 401 && !retried) {
            const refreshed = await refreshSession();
            if (refreshed) return run(true);
          }
          return res;
        };
        return run(false);
      },
    }),
  ],
});
